'use client';

import { useState, useRef, useEffect, ChangeEvent } from 'react';
import { motion } from 'framer-motion';
import { Upload, X, Image as ImageIcon, File as FileIcon } from 'lucide-react';

interface FileUploadProps {
  label?: string;
  value?: string; // URL of existing image
  onChange?: (file: File | null, previewUrl: string | null) => void;
  accept?: string;
  maxSize?: number; // in MB
  className?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  /** image: images only (default). any: allow all file types (with size limit). */
  mode?: 'image' | 'any';
}

export function FileUpload({
  label,
  value,
  onChange,
  accept = 'image/*',
  maxSize = 5,
  className = '',
  required = false,
  hint,
  error,
  mode = 'image',
}: FileUploadProps) {
  const [preview, setPreview] = useState<string | null>(value || null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [hasNewFile, setHasNewFile] = useState(false);
  const prevValueRef = useRef<string | undefined>(value);
  const resolvedAccept = mode === 'any' ? (accept === 'image/*' ? '*/*' : accept) : accept;

  // Sync preview with value prop when it changes from external source
  useEffect(() => {
    if (value !== undefined) {
      const newPreview = value || null;
      const currentValue = prevValueRef.current;
      
      // Only update if value actually changed
      if (currentValue !== value) {
        console.log('FileUpload: Value prop changed', { 
          oldValue: currentValue?.substring(0, 50) || 'undefined', 
          newValue: newPreview?.substring(0, 50) || 'null',
          hasNewFile,
          currentPreview: preview?.substring(0, 50) || 'null'
        });
        
        // Always update preview when value prop changes from external source
        // Only update if we don't have a new file selected
        if (!hasNewFile) {
          setPreview(newPreview);
          prevValueRef.current = value;
        }
      }
    }
  }, [value]);
  
  // Reset hasNewFile when value changes from external source
  useEffect(() => {
    if (value !== undefined && value !== prevValueRef.current && !hasNewFile) {
      setHasNewFile(false);
    }
  }, [value, hasNewFile]);

  const handleFileSelect = (file: File | null) => {
    if (!file) {
      setPreview(null);
      setSelectedFileName(null);
      setHasNewFile(false);
      onChange?.(null, null);
      return;
    }

    if (mode === 'image' && !file.type.startsWith('image/')) {
      alert(`Please select an image file. Selected: ${file.type}`);
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSize) {
      alert(`File size must be less than ${maxSize}MB. Selected file: ${fileSizeMB.toFixed(2)}MB`);
      return;
    }

    setSelectedFileName(file.name);
    setHasNewFile(true);

    if (mode === 'any' && !file.type.startsWith('image/')) {
      setPreview(null);
      onChange?.(file, null);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const previewUrl = reader.result as string;
      setPreview(previewUrl);
      onChange?.(file, previewUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreview(null);
    setSelectedFileName(null);
    setHasNewFile(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onChange?.(null, null);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className={`space-y-1.5 ${className}`}
    >
      {label && (
        <label className="flex items-center gap-1 text-xs font-bold text-gray-700 uppercase tracking-wide">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
        className={`
          relative border-2 border-dashed rounded-lg p-4 cursor-pointer
          transition-all duration-200
          ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-gray-50'}
          ${error ? 'border-red-300 bg-red-50/50' : ''}
          hover:border-indigo-400 hover:bg-indigo-50/50
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={resolvedAccept}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0] || null;
            handleFileSelect(file);
          }}
          className="hidden"
        />

        {preview ? (
          <div className="relative">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-48 object-cover rounded-lg"
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            >
              <X size={16} />
            </button>
            <div className="mt-2 text-center">
              <p className="text-xs text-gray-500">Click to change file</p>
            </div>
          </div>
        ) : selectedFileName ? (
          <div className="relative text-center py-8">
            <FileIcon size={40} className="mx-auto mb-3 text-indigo-500" />
            <p className="text-sm font-medium text-gray-900 break-all px-4">{selectedFileName}</p>
            <p className="text-xs text-gray-500 mt-1">Click to change file</p>
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        ) : value && mode === 'any' ? (
          <div className="relative text-center py-8">
            <FileIcon size={40} className="mx-auto mb-3 text-gray-400" />
            <p className="text-sm font-medium text-gray-700 break-all px-4">
              {value.split('/').pop()}
            </p>
            <p className="text-xs text-gray-500 mt-1">Click to replace file</p>
          </div>
        ) : (
          <div className="text-center py-8">
            {mode === 'any' ? (
              <Upload size={40} className="mx-auto mb-3 text-gray-400" />
            ) : (
              <ImageIcon size={40} className="mx-auto mb-3 text-gray-400" />
            )}
            <p className="text-sm font-medium text-gray-700 mb-1">
              Click to upload or drag and drop
            </p>
            <p className="text-xs text-gray-500">
              {mode === 'any'
                ? `Any file type up to ${maxSize}MB`
                : `PNG, JPG, GIF up to ${maxSize}MB`}
            </p>
          </div>
        )}
      </div>

      {hint && !error && (
        <p className="text-xs text-gray-400">{hint}</p>
      )}
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -3 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xs text-red-600 font-medium"
        >
          {error}
        </motion.p>
      )}
    </motion.div>
  );
}

